import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MessageSquare, X, Star } from "lucide-react";
import api from "@/lib/api";

/**
 * A small, non-intrusive floating button that lets logged-in users leave feedback.
 * Persists to /api/feedback (Mongo). No external service.
 */
export default function FeedbackButton() {
    const [open, setOpen] = useState(false);
    const [msg, setMsg] = useState("");
    const [rating, setRating] = useState(0);
    const [credit, setCredit] = useState(true);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!msg.trim()) {
            toast.error("write a few words first");
            return;
        }
        setBusy(true);
        try {
            await api.post("/feedback", {
                message: msg.trim(),
                rating: rating || null,
                context: window.location.pathname.replace("/", "") || "home",
                credit,
            });
            toast.success("thank you — Pax reads every note.");
            setMsg("");
            setRating(0);
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "couldn't send. try again?");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                data-testid="feedback-open"
                onClick={() => setOpen(true)}
                className="fixed bottom-5 right-5 z-40 bg-white border border-clayHover text-ink rounded-full pl-3 pr-4 py-2 shadow-soft hover:shadow-clay hover:border-terracotta transition-all flex items-center gap-2 font-body text-sm"
                aria-label="Leave feedback"
            >
                <MessageSquare className="w-4 h-4 text-terracotta" />
                <span className="hidden sm:inline">feedback</span>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/25 backdrop-blur-[2px] p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setOpen(false)}
                        data-testid="feedback-backdrop"
                    >
                        <motion.div
                            onClick={(e) => e.stopPropagation()}
                            initial={{ y: 40, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 40, opacity: 0 }}
                            transition={{ type: "spring", damping: 22, stiffness: 260 }}
                            className="bg-bone rounded-3xl border border-clayHover shadow-clay w-full max-w-md p-6 relative"
                            data-testid="feedback-modal"
                        >
                            <button
                                onClick={() => setOpen(false)}
                                className="absolute top-4 right-4 text-inkSoft hover:text-ink"
                                aria-label="Close feedback"
                                data-testid="feedback-close"
                            >
                                <X className="w-4 h-4" />
                            </button>

                            <h2 className="font-heading text-2xl tracking-tight text-ink mb-1">a few words for Pax?</h2>
                            <p className="text-sm text-inkSoft font-body mb-5">
                                bugs, ideas, or just a thought — everything helps shape what Pax becomes.
                            </p>

                            <div className="flex items-center gap-2 mb-4" data-testid="feedback-stars">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        onClick={() => setRating(n === rating ? 0 : n)}
                                        className="p-1"
                                        aria-label={`Rate ${n} out of 5`}
                                        data-testid={`feedback-star-${n}`}
                                    >
                                        <Star
                                            className={`w-6 h-6 transition-all ${
                                                n <= rating
                                                    ? "text-terracotta fill-terracotta"
                                                    : "text-inkSoft/40 hover:text-terracotta"
                                            }`}
                                        />
                                    </button>
                                ))}
                            </div>

                            <textarea
                                value={msg}
                                onChange={(e) => setMsg(e.target.value)}
                                rows={4}
                                maxLength={2000}
                                placeholder="what worked, what didn't, what you'd love to see…"
                                className="w-full bg-white border border-clayHover rounded-2xl px-4 py-3 outline-none focus:border-terracotta font-body text-sm text-ink resize-none"
                                data-testid="feedback-textarea"
                            />
                            <label className="flex items-center gap-2.5 mt-3 cursor-pointer font-body text-xs text-inkSoft">
                                <input
                                    type="checkbox"
                                    checked={credit}
                                    onChange={(e) => setCredit(e.target.checked)}
                                    data-testid="feedback-credit-checkbox"
                                    className="accent-terracotta w-3.5 h-3.5"
                                />
                                credit my first name on the tester thank-you wall
                            </label>
                            <div className="flex items-center justify-between mt-4">
                                <span className="text-xs text-inkSoft font-body">{msg.length}/2000</span>
                                <button
                                    onClick={submit}
                                    disabled={busy}
                                    data-testid="feedback-submit"
                                    className="bg-terracotta text-bone px-5 py-2.5 rounded-full hover:bg-terracottaHover disabled:opacity-60 transition-colors font-body text-sm"
                                >
                                    {busy ? "sending…" : "send"}
                                </button>
                            </div>
                            <a
                                href="/board"
                                data-testid="feedback-board-link"
                                className="block mt-4 text-center text-xs text-inkSoft hover:text-terracotta font-body underline-offset-4 hover:underline"
                            >
                                see what we're already fixing →
                            </a>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
